import type { Field } from 'factorial-form'
import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import type { Return } from 'modules/core/lib/asyncAction'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { trackEditShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'
import type Shift from 'modules/shift_management/store/internal/models/Shift'
import { useBulkCudPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCudPlannedBreaks'

import buildAddPlannedBreaksParameters from '../lib/buildAddPlannedBreaksParameters'
import type PlannedBreakForm from '../lib/forms/PlannedBreakForm'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

export interface BulkPatchProps {
  shifts: Shift[]
  name: string | null
  startAt: string | null
  endAt: string | null
  notes: string | null
  extraHours: boolean | null
  locationId: number | null
  workAreaId: number | null
  mixedValueNotes: number
}

export const buildEditShiftsParameters = (
  shifts: Shift[],
  formName: Field,
  formStartAt: Field,
  formEndAt: Field,
  formExtraHours: boolean | null,
  formLocationId: Field,
  formWorkAreaId: Field,
  formNotes: Field,
  uniqNotesLength: number
): BulkPatchProps => {
  const name = formName.value || formName.isDirty ? formName.value : null
  const startAt = formStartAt.value || null
  const endAt = formEndAt.value || null
  const notes = formNotes.value || formNotes.isDirty ? formNotes.value : null

  // For amplitude tracking purposes, we have to keep the original note values
  // in mind whether they were unique or not, but also listen to any changes
  // the user makes before submitting the form.
  const mixedValueNotes = uniqNotesLength > 1 && !formNotes.isDirty ? 1 : 0

  const locationId = formLocationId.value || null
  const workAreaId = formWorkAreaId.value || null

  return {
    shifts,
    name,
    startAt,
    endAt,
    extraHours: formExtraHours,
    notes,
    locationId,
    workAreaId,
    mixedValueNotes,
  }
}

export interface ReturnProps {
  onEdit: (closePortal?: ClosePortalFn) => Promise<Return<void>>
  submitting: boolean
}

const useEditShifts = (
  {
    shifts,
    name,
    startAt,
    endAt,
    notes,
    extraHours,
    locationId,
    workAreaId,
    mixedValueNotes,
  }: BulkPatchProps,
  plannedBreakForms: PlannedBreakForm[],
  through: 'modal' | 'side_panel',
  contractVersions: ContractVersions
): ReturnProps => {
  const [{ shiftsRows }] = useShiftsRows()
  const dispatchSelection = useSelectionDispatch()

  const [{ view }] = useSchedule()
  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)
  const { bulkCud } = useBulkCudPlannedBreaks()
  const shiftIds = shifts.map((s) => s.id)

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal?: ClosePortalFn) => {
      await shiftsCollection.rpc('bulk_patch', {
        shift_ids: shiftIds,
        name: name,
        start_at: startAt,
        end_at: endAt,
        notes: notes,
        extra_hours: extraHours,
        location_id: locationId,
        work_area_id: workAreaId,
      })

      // Edit planned breaks
      if (plannedBreakForms && shifts.length === 1) {
        const shiftId = shifts[0]?.id
        const plannedBreaks = buildAddPlannedBreaksParameters(plannedBreakForms, shifts)
        await asyncAction(() => bulkCud({ plannedBreaksParams: [{ shiftId, plannedBreaks }] }), {
          successFlash: false,
        })
      }

      const assignedToModified = false
      const originalEmployeeIncluded = false
      const totalNumberOfAssignedEmployees = 0

      await trackEditShiftSubmit({
        shifts,
        name,
        startAt,
        endAt,
        notes,
        extraHours,
        locationId,
        workAreaId,
        mixedValueNotes,
        view,
        assignedToModified,
        originalEmployeeIncluded,
        totalNumberOfAssignedEmployees,
        through,
      })

      // Get all shift rows that have shifts by employees affected by the edit action to update stats
      const shiftsEmployeeIds = uniq(shifts.map((shift) => shift.employeeId))
      const relatedShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
        const employeeIds = new Set(row.employeeIds)
        return shiftsEmployeeIds.some((id: number) => employeeIds.has(id))
      })
      await refreshShiftsRows(relatedShiftsRows, undefined)

      if (closePortal) closePortal()

      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })
    },
    {
      successMessage: i18n.t('shift_management.actions.edit_shifts_success'),
    }
  )

  return {
    onEdit: performAction,
    submitting,
  }
}

export default useEditShifts
